import { isCoachingRole, type CoachingRole } from "../domain/roles";
import { isEvidenceModeEnabled } from "../lib/config";
import { ApiClientError } from "./api-client";
import type {
  CoachingProfileDto,
  OtpRequest,
  OtpVerifyRequest,
  ProfileUpdateRequest
} from "./coaching-dtos";

type MockRequestOptions = { method?: string; body?: BodyInit | null; idempotencyKey?: string };
const PLATFORM_ADMIN_EVIDENCE_ROLES: ReadonlySet<CoachingRole> = new Set<CoachingRole>(["super_admin", "admin"]);

interface MockAuthResult {
  handled: boolean;
  value?: unknown;
}

export function createMockAuthStore() {
  let authenticated = true;
  let profile: CoachingProfileDto = {
    id: "mock-admin-1",
    email: "admin@coaching.example",
    name: "Admin Operator",
    role: "admin",
    isPlatformAdmin: true,
    orgId: "global",
    title: "Operations lead",
    phone: "+1 555 0100",
    timezone: "America/New_York",
    notification_preferences: {
      session_reminders: true,
      calendar_alerts: true,
      weekly_digest: false
    }
  };

  // Evidence role override: the base persona is a platform-admin operator, but only
  // the staff-admin roles keep that flag; coach and customer roles are never platform admins.
  function currentProfile(): CoachingProfileDto {
    const role = evidenceRoleOverride();
    if (!role) return profile;
    return { ...profile, role, isPlatformAdmin: profile.isPlatformAdmin === true && PLATFORM_ADMIN_EVIDENCE_ROLES.has(role) };
  }

  return {
    getProfile(): CoachingProfileDto {
      return currentProfile();
    },
    handle(pathname: string, options: MockRequestOptions = {}): MockAuthResult {
      if (pathname === "/auth/me") {
        if (!authenticated) throw new ApiClientError("Not authenticated", 401, "mock-auth-cleared");
        return { handled: true, value: currentProfile() };
      }
      if (pathname === "/otp/send") {
        const body = parseBody<OtpRequest>(options.body);
        return {
          handled: true,
          value: {
            ok: Boolean(body.email),
            status: body.email ? "requested" : "rate_limited",
            reference: "mock-otp-requested"
          }
        };
      }
      if (pathname === "/otp/verify") {
        const body = parseBody<OtpVerifyRequest>(options.body);
        if (body.otp !== "123456") {
          authenticated = false;
          return { handled: true, value: { ok: false, status: "invalid", reference: "mock-invalid-otp" } };
        }
        authenticated = true;
        profile = { ...profile, email: body.email || profile.email };
        return { handled: true, value: { ok: true, status: "verified", profile: currentProfile(), reference: "mock-auth-verified" } };
      }
      if (pathname === "/auth/logout") {
        authenticated = false;
        return { handled: true, value: { ok: true } };
      }
      if (pathname === "/profile") {
        if (!authenticated) throw new ApiClientError("Not authenticated", 401, "mock-auth-cleared");
        const result = validateProfileUpdate(parseBody<ProfileUpdateRequest>(options.body), profile);
        if (!result.ok) return { handled: true, value: result };
        profile = { ...profile, ...result.profile };
        return { handled: true, value: { ...result, profile: currentProfile(), reference: options.idempotencyKey ?? "mock-profile-saved" } };
      }

      return { handled: false };
    }
  };
}

function evidenceRoleOverride(): CoachingRole | null {
  if (!isEvidenceModeEnabled() || typeof window === "undefined") return null;
  const value = new URLSearchParams(window.location.search).get("role");
  return isCoachingRole(value) ? value : null;
}

function validateProfileUpdate(body: ProfileUpdateRequest, profile: CoachingProfileDto) {
  const field_errors: NonNullable<import("./coaching-dtos").ProfileUpdateResult["field_errors"]> = {};

  if (!body.name || body.name.trim().length < 2) field_errors.name = "Enter at least two characters.";
  if (!body.phone || !/^\+?[0-9 ()-]{7,}$/.test(body.phone)) field_errors.phone = "Enter a reachable phone number.";
  if (!["America/New_York", "Europe/London", "Asia/Tokyo"].includes(body.timezone)) {
    field_errors.timezone = "Choose a supported timezone.";
  }
  if (!body.notification_preferences) {
    field_errors.notification_preferences = "Choose notification preferences.";
  }

  if (Object.keys(field_errors).length > 0) {
    return { ok: false as const, status: "validation_error" as const, field_errors, reference: "mock-profile-validation" };
  }

  return {
    ok: true as const,
    status: "saved" as const,
    profile: {
      ...profile,
      name: body.name.trim(),
      title: body.title?.trim() || undefined,
      phone: body.phone.trim(),
      timezone: body.timezone,
      notification_preferences: body.notification_preferences
    }
  };
}

function parseBody<T>(body: BodyInit | null | undefined): T {
  if (typeof body === "string" && body.trim()) return JSON.parse(body) as T;
  return {} as T;
}
